import { z } from "zod"

export const phoneVerificationStates = ["UNVERIFIED", "VERIFIED", "REVERIFICATION_REQUIRED"] as const
export const checkoutEligibilities = ["ELIGIBLE", "PHONE_VERIFICATION_REQUIRED", "PHONE_REVERIFICATION_REQUIRED", "BLOCKED"] as const
export const phoneCredentialLinkReasons = ["INITIAL_VERIFICATION", "NUMBER_CHANGE", "REVERIFICATION"] as const
export const ownedRedemptionLifecycleStates = ["PREPARING", "ISSUING", "SUCCESS", "FAILURE", "NEEDS_REVIEW", "CAP_EXHAUSTED"] as const

export const phoneVerificationStateSchema = z.enum(phoneVerificationStates)
export const checkoutEligibilitySchema = z.enum(checkoutEligibilities)
export const phoneCredentialLinkReasonSchema = z.enum(phoneCredentialLinkReasons)
export const ownedRedemptionLifecycleSchema = z.enum(ownedRedemptionLifecycleStates)
export const productIdSchema = z.uuid().brand("ProductId")
export const quoteIdSchema = z.uuid().brand("QuoteId")
export const redemptionIdSchema = z.uuid().brand("RedemptionId")
export const maskedMmsRecipientSchema = z
  .string()
  .regex(/^01[0-9]-\*{4}-[0-9]{4}$/)
  .brand("MaskedMmsRecipient")
export const safeInternalReturnToSchema = z
  .string()
  .regex(/^\/(?:my\/phone|redeem\/confirm(?:\?quoteId=[A-Za-z0-9_-]{1,128})?)$/)
  .brand("SafeInternalReturnTo")

const checkoutPhoneFields = {
  checkoutEligibility: checkoutEligibilitySchema,
  maskedRecipient: maskedMmsRecipientSchema.nullable(),
  verificationState: phoneVerificationStateSchema,
}

export const phoneVerificationStatusSchema = z.strictObject(checkoutPhoneFields)
export const redemptionCheckoutReadinessSchema = phoneVerificationStatusSchema
export const phoneCredentialLinkCompletionTransportSchema = z.strictObject({
  firebaseIdToken: z.string().min(1).max(20_000),
  linkReason: phoneCredentialLinkReasonSchema,
  returnTo: safeInternalReturnToSchema,
  tokenRefresh: z.literal("FORCE_REFRESHED"),
})
export const phoneVerificationCompletionSchema = z.strictObject({
  checkoutEligibility: checkoutEligibilitySchema,
  maskedRecipient: maskedMmsRecipientSchema,
  returnTo: safeInternalReturnToSchema,
  verificationState: z.literal("VERIFIED"),
})
export const phoneVerificationChallengeRequestSchema = z.strictObject({ phoneNumber: z.string().regex(/^010\d{8}$/) })
export const phoneVerificationChallengeResponseSchema = z.strictObject({ cooldownSeconds: z.number().int().min(1).max(3600), kind: z.literal("allowed"), remainingRequests: z.number().int().nonnegative() })
export const mobileRedemptionCommandReceiptSchema = z.strictObject({
  kind: z.literal("resource"),
  nextRoute: z.string().regex(/^\/redeem\/(?:confirm\?quoteId|processing\?resourceId)=[0-9a-f-]{36}$/),
  resourceId: z.uuid(),
})
export const ownedRedemptionQuoteSchema = z.strictObject({
  amountWon: z.number().int().positive(),
  expiresAt: z.iso.datetime(),
  productId: productIdSchema,
  productName: z.string().min(1),
  quoteId: quoteIdSchema,
})
export const ownedRedemptionQuoteResponseSchema = ownedRedemptionQuoteSchema
export const ownedRedemptionLifecycleResponseSchema = z.strictObject({ lifecycle: ownedRedemptionLifecycleSchema, orderId: redemptionIdSchema })
export const todo26ProductSchema = z.strictObject({ imageUrl: z.url(), internalProductId: productIdSchema, name: z.string().min(1), priceWon: z.number().int().positive(), supplierCode: z.string().min(1) })
export const todo26BalanceSchema = z.strictObject({ availableWon: z.number().int().nonnegative(), expiring30DayWon: z.number().int().nonnegative(), expiring90DayWon: z.number().int().nonnegative(), heldWon: z.number().int().nonnegative(), totalWon: z.number().int().nonnegative() })

export type PhoneVerificationState = z.infer<typeof phoneVerificationStateSchema>
export type PhoneCredentialLinkReason = z.infer<typeof phoneCredentialLinkReasonSchema>
export type PhoneVerificationStatus = Readonly<z.infer<typeof phoneVerificationStatusSchema>>
export type PhoneCredentialLinkCompletionTransport = Readonly<z.infer<typeof phoneCredentialLinkCompletionTransportSchema>>
export type PhoneVerificationChallengeResponse = Readonly<z.infer<typeof phoneVerificationChallengeResponseSchema>>
export type MobileRedemptionCommandReceipt = Readonly<z.infer<typeof mobileRedemptionCommandReceiptSchema>>
export type OwnedRedemptionQuote = Readonly<z.infer<typeof ownedRedemptionQuoteSchema>>
export type OwnedRedemptionQuoteResponse = OwnedRedemptionQuote
export type OwnedRedemptionLifecycle = z.infer<typeof ownedRedemptionLifecycleSchema>
export type OwnedRedemptionLifecycleResponse = Readonly<z.infer<typeof ownedRedemptionLifecycleResponseSchema>>
export type Todo26Product = Readonly<z.infer<typeof todo26ProductSchema>>
export type Todo26Balance = Readonly<z.infer<typeof todo26BalanceSchema>>
